import React from "react";
import { useTranslation } from "react-i18next";
import { Header } from "../header";
import { useContactInfo } from "@/api/information";
import { MailIcon } from "@/assets/icons/MailIcon";
import { PhoneIcon } from "@/assets/icons/PhoneIcon";
import ContactCard from "../contactCard";
import { AppShell } from "@/components/Navbar";
import clsx from "clsx";
import { useScrollToShowAppShell } from "../hooks/useScrollToShowAppShell";
import { Helmet } from "react-helmet-async";

export const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const { data: contact } = useContactInfo();
  const { isScrolled, headerRef } = useScrollToShowAppShell();

  return (
    <>
      <Helmet>
        <title>Contact Us | Egyptian Food Bank - Get in Touch</title>
        <meta
          name="description"
          content="Need to get in touch with Egyptian Food Bank? Find all the contact details you need here, including phone and email address. We're here to answer any questions and listen to your feedback."
        />
        <meta
          name="keywords"
          content="Contact Egyptian Food Bank, NGO contact, get in touch, help, support, customer service"
        />
        <link rel="canonical" href="https://efb.eg/contact" />
        <meta property="og:title" content="Contact Us | Egyptian Food Bank" />
        <meta
          property="og:description"
          content="Reach out to Egyptian Food Bank for inquiries, support, or feedback. Find all our contact information on this page and connect with us today."
        />
        <meta property="og:url" content="https://efb.eg/contact" />
      </Helmet>
      <div ref={headerRef}>
        <Header
          scrollHref="#contact-cards"
          title={t("getInTouchPage.title")}
          picture="contact.jpg"
        />
      </div>

      <AppShell
        navbarClass={clsx(
          "fixed top-0 -z-10 w-full bg-[#CDCFC9] opacity-0 transition-opacity duration-300 ease-in-out",
          { "opacity-100 z-10": isScrolled },
        )}
      />

      <div
        id="contact-cards"
        className="mx-4 my-8 flex flex-col justify-center gap-6 md:mx-0 md:my-0 md:mb-16 md:flex-row md:px-6 md:pt-16"
      >
        <ContactCard
          IconComponent={MailIcon}
          headerText={t("information.chat")}
          contactInfo={contact?.email}
          linkType="mailto"
        />

        <ContactCard
          IconComponent={PhoneIcon}
          headerText={t("information.call")}
          contactInfo={contact?.number}
          linkType="tel"
        />
      </div>
    </>
  );
};
